// @flow
import { get } from 'lodash';
import * as React from 'react';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import {
    defineMessages, FormattedMessage, injectIntl, IntlShape,
} from 'react-intl';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import type { Confirmation } from 'speed-js-react';
import { withConfirmation, connectAPI } from 'speed-js-react';
import * as sidePanelActions from 'esm-web-pro/redux/side-panel/actions';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import SidePane from 'esm-web-pro/components/layout/SidePane';
import MainActions from 'esm-web-pro/components/layout/sidePanel/MainActions';
import BreakSpliceIcon from 'esm-web-pro/components/layout/sidePanel/icons/BreakSpliceIcon';
import EditIcon from 'esm-web-pro/components/layout/sidePanel/icons/EditIcon';
import TimelineIcon from 'esm-web-pro/components/layout/sidePanel/icons/TimelineIcon';
import type { Match, RouterHistory } from 'react-router-dom';
import point14Messages from 'esm-web-pro/messages/points14Messages';
import Point14Editor from './Point14Editor';


type SidePanelActionProps = {
    openPanel: (key: string, url: ?string, skipSensitivity?: boolean) => { key: string, url: string },
    closePanel: (key: string, skipSensitivity: boolean) => { key: string },
}

type Props = {
    ...$Exact<SidePanelActionProps>,
    point14: Object,
    onEdit: Function,
    confirm: (confirmation: Confirmation) => void,
    intl: IntlShape,
    history: RouterHistory,
    match: Match,
    map: Object,
    deleteObject?: Object,
    resource: Object,
    // eslint-disable-next-line react/no-unused-prop-types
    updateObject: Function,
    editor: Object,
}

const msg = defineMessages({
    deleteTitle: {
        id: 'delete-splice-title',
        defaultMessage: 'Are you sure you want to delete the splice?',
    },
    cancelDelete: {
        id: 'delete-splice-cancel',
        defaultMessage: 'Cancel',
    },
    confirmDelete: {
        id: 'delete-splice-confirm',
        defaultMessage: 'Confirm',
    },
});

const POINT14_EDIT_KEY = 'SPLICE_EDIT';

class Point14Actions extends React.Component <Props> {
    handleClose = (skipSensitivity = false) => {
        const { closePanel } = this.props;
        closePanel(POINT14_EDIT_KEY, skipSensitivity);
    };

    handleSave = (newValues) => {
        const { onEdit } = this.props;
        onEdit(newValues);
        this.handleClose(true);
    };

    getActions = () => {
        const {
            map: { is_draft: isDraft }, intl: { formatMessage }, point14, openPanel, match, history, editor,
        } = this.props;
        const regex = /^(.+?)(\/+)$/gm;
        const actions = [];
        if (isDraft) {
            actions.push(
                {
                    key: `${point14.id}-edit`,
                    icon: <EditIcon size="small" />,
                    callback: () => openPanel(POINT14_EDIT_KEY, '', match.url, true),
                    tooltip: formatMessage(actionMessages.edit),
                    disabled: editor.state.loading,
                    marginVertical: '15px',
                    radius: '0%',
                    color: 'transparent',
                },
            );
        }

        actions.push({
            key: 'splice-timeline',
            icon: <TimelineIcon />,
            tooltip: formatMessage(actionMessages.timeline),
            callback: () => history.push(`${match.url
                .replace(regex, '$1')}/timeline/${point14.type}/${point14.identity}`),
            marginVertical: '15px',
            radius: '0%',
            color: 'transparent',
        });

        if (isDraft && point14.type === 'splice') {
            actions.push({
                key: `${point14.id}-delete`,
                icon: <BreakSpliceIcon size="small" />,
                callback: this.deleteSplice,
                tooltip: formatMessage(actionMessages.break),
                disabled: editor.state.loading,
                marginVertical: '15px',
                radius: '0%',
                color: 'transparent',
            });
        }

        return actions;
    };

    deleteSplice = () => {
        const {
            point14, deleteObject, intl: { formatMessage }, confirm, resource, history, match,
        } = this.props;
        confirm({
            message: formatMessage(msg.deleteTitle),
            actions: {
                cancel: {
                    label: formatMessage(msg.cancelDelete),
                    callback: () => {
                    },
                    type: 'negative',
                },
                confirm: {
                    label: formatMessage(msg.confirmDelete),
                    callback: () => {
                        if (deleteObject) {
                            deleteObject(`${resource}`, point14.uuid).then(
                                history.push(match.url.split('/layer')[0]),
                            );
                        }
                    },
                    type: 'positive',
                },
            },
        });
    };

    render() {
        const {
            point14, resource, history, match, updateObject, intl: { formatMessage },
        } = this.props;

        return (
            <>
                <SidePane
                    id={POINT14_EDIT_KEY}
                    key={point14.uuid}
                    title={(
                        <FormattedMessage
                            id="point14-editor-title"
                            defaultMessage="Edit {type}"
                            values={{ type: formatMessage(point14Messages.opticalport).toLowerCase() }}
                        />
                    )}
                >
                    <Point14Editor
                        id={POINT14_EDIT_KEY}
                        point14={point14}
                        onClose={this.handleClose}
                        onSave={this.handleSave}
                        resource={resource}
                        match={match}
                        history={history}
                        updateObject={updateObject}
                    />
                </SidePane>
                <MainActions main actions={this.getActions()} />
            </>
        );
    }
}

Point14Actions.defaultProps = {
    deleteObject: undefined,
};

const apiCallback = (apiClient: ApiClient) => ({
    deleteObject: {
        send: (resource: string, id: string) => get(apiClient.resources,
            resource).delete(id),
    },
});

const mapDispatchToProps = (dispatch) => (
    bindActionCreators(sidePanelActions, dispatch)
);

export default connectAPI(apiCallback)(
    withRouter(
        withConfirmation(
            injectIntl(
                connect(null, mapDispatchToProps)(Point14Actions),
            ),
        ),
    ),
);
